import Pagination from "../common/Pagination";
import React, { useEffect } from "react";
import { renderSortField } from "../common/RenderFunctions";
import { DataGrid, GridColDef } from "@mui/x-data-grid";
import Paper from "@mui/material/Paper";
import { GridPaginationState } from "@mui/x-data-grid/hooks/features/pagination/gridPaginationInterfaces";

interface IProps {
  orders: Order[];
  page: number;
  perPage?: number;
  setPerPage?: (perPage: number) => void;
  sortBy: string;
  setSortBy: (sortBy: string) => void;
  sortOrder: "asc" | "desc";
  setSortOrder: (sortOrder: "asc" | "desc") => void;
  totalPages: number;
  totalResults?: number;
  loading: boolean;
  handlePageChange: (newPage: number) => void;
  openManageOrderModal: (order: Order) => void;
}

const ListOrderItems: React.FC<IProps> = ({
  orders,
  page,
  perPage,
  setPerPage,
  totalPages,
  sortBy,
  setSortBy,
  sortOrder,
  setSortOrder,
  handlePageChange,
  openManageOrderModal,
  totalResults,
  loading,
}: IProps) => {
  const [showListedFields, setShowListedFields] = React.useState<
    Map<string, boolean>
  >(() => {
    const session = sessionStorage.getItem("orders_listed_fields");
    if (session) {
      return new Map<string, boolean>(JSON.parse(session));
    }
    return new Map<string, boolean>([
      ["id", true],
      ["offer_external_id", true],
      ["type", true],
      ["compound_name", true],
      ["customer_id", true],
      ["customer_name", true],
      ["requested_by", true],
      ["commercial_portfolio", true],
      ["commercial_agent", true],
      ["amount", true],
      ["status", true],
      ["created_at", true],
      ["updated_at", true],
    ]);
  });

  function getOrderTypeLabel(order: Order): string {
    if (order.order_type === "DEFAULT") {
      return "Revendedor";
    } else if (order.order_type === "INTERNAL") {
      if (order.offer_client_type === "RESELLER") {
        return "Comercial";
      } else if (order.offer_client_type === "END_CUSTOMER") {
        return "Interna";
      }
    }
    return "Outro";
  }

  function getOrderTypeClass(order: Order): string {
    if (order.order_type === "DEFAULT") {
      return "bg-amber-300";
    } else if (order.order_type === "INTERNAL") {
      if (order.offer_client_type === "RESELLER") {
        return "bg-blue-300";
      } else if (order.offer_client_type === "END_CUSTOMER") {
        return "bg-green-300";
      }
    }
    return "bg-gray-300";
  }

  function getStatusLabel(status: string): string {
    switch (status) {
      case "PENDING":
        return "Pendente";
      case "CONFIRMED":
        return "Confirmada";
      case "REJECTED":
        return "Rejeitada";
      case "SHIPPED":
        return "Enviada";
      case "COMPLETED":
        return "Concluída";
      default:
        return "status desconhecido";
    }
  }

  const columns: GridColDef[] = [
    {
      field: "id",
      headerName: "ID",
      width: 70,
      sortable: true,
      valueGetter: (value, row) => row.id || "n/a",
    },
    {
      field: "offer_external_id",
      headerName: "Proposta",
      width: 120,
      sortable: true,
      valueGetter: (value, row) => row.offer_external_id || "n/a",
    },
    {
      field: "type",
      headerName: "Origem",
      width: 110,
      sortable: true,
      renderCell: (params) => (
        <small
          className={`rounded-3xl px-2 p-1 has-text-weight-bold ${getOrderTypeClass(params.row)}`}
        >
          {getOrderTypeLabel(params.row)}
        </small>
      ),
    },
    {
      field: "compound_name",
      headerName: "Computador",
      width: 130,
      sortable: true,
      valueGetter: (value, row) => row.items[0]?.compound_id || "n/a",
    },
    {
      field: "customer_id",
      headerName: "Cod Cliente",
      width: 120,
      sortable: true,
      valueGetter: (value, row) =>
        row.order_type == "DEFAULT"
          ? row.erp_client_id
          : (row.offer_client?.id ?? "n/a"),
    },
    {
      field: "customer_name",
      headerName: "Nome do Cliente",
      width: 160,
      sortable: true,
      valueGetter: (value, row) =>
        row.order_type == "DEFAULT"
          ? row.client?.name
          : (row.offer_client?.name ?? "-"),
    },
    {
      field: "requested_by",
      headerName: "Vendedor",
      width: 120,
      sortable: true,
      valueGetter: (value, row) => row.requested_by_name || row.requested_by,
    },
    {
      field: "commercial_portfolio",
      headerName: "Cod Comercial",
      width: 120,
      sortable: true,
      valueGetter: (value, row) => row.commercial_portfolio || "-",
    },
    {
      field: "commercial_agent",
      headerName: "Comercial",
      width: 160,
      sortable: true,
      renderCell: (params) =>
        params.row.commercial_agents.length > 0 ? (
          params.row.commercial_agents.map(
            (agent: { id: string; name: string | null; username: string }) => (
              <span
                key={agent.id}
                className="inline-block mr-1 rounded bg-blue-100 px-2 py-1 text-xs font-semibold text-blue-800"
              >
                {agent.name || agent.username}
              </span>
            ),
          )
        ) : (
          <small>-</small>
        ),
    },
    {
      field: "amount",
      headerName: "Valor Total",
      width: 120,
      sortable: true,
      type: "number",
      valueGetter: (value, row) =>
        row.total_amount.toLocaleString("pt-PT", {
          style: "currency",
          currency: "EUR",
        }),
    },
    {
      field: "status",
      headerName: "Estado",
      width: 110,
      sortable: true,
      valueGetter: (value, row) => getStatusLabel(row.status),
    },
    {
      field: "created_at",
      headerName: "Criada em",
      width: 140,
      sortable: true,
      valueGetter: (value, row) =>
        row?.created_at
          ? new Date(row.created_at).toLocaleString("pt-PT", {
              day: "2-digit",
              month: "2-digit",
              year: "2-digit",
              hour: "2-digit",
              minute: "2-digit",
            })
          : "",
    },
    {
      field: "updated_at",
      headerName: "Alterada em",
      width: 140,
      sortable: true,
      valueGetter: (value, row) =>
        row?.updated_at
          ? new Date(row.updated_at).toLocaleString("pt-PT", {
              day: "2-digit",
              month: "2-digit",
              year: "2-digit",
              hour: "2-digit",
              minute: "2-digit",
            })
          : "",
    },
  ];

  const paginationState: GridPaginationState = {
    paginationMode: "server",
    paginationModel: {
      page: page - 1,
      pageSize: perPage || 10,
    },
    rowCount: totalResults || -1,
    enabled: true,
    meta: {
      hasNextPage: true,
    },
  };

  const handlePaginationChange = (model: {
    page: number;
    pageSize: number;
  }) => {
    if (model.pageSize !== perPage && setPerPage) {
      setPerPage(model.pageSize);
    }
    if (model.page + 1 !== page) {
      handlePageChange(model.page + 1);
    }
  };

  // Save showListedFields to sessionStorage when it changes
  React.useEffect(() => {
    sessionStorage.setItem(
      "orders_listed_fields",
      JSON.stringify(Array.from(showListedFields.entries())),
    );
  }, [showListedFields]);

  console.log("Current totalResults:", totalResults);
  return (
    <Paper sx={{ height: "screen", width: "100%" }}>
      <DataGrid
        rows={orders}
        columns={columns}
        loading={loading}
        density="standard"
        initialState={{ pagination: paginationState }}
        rowCount={totalResults || 0}
        paginationMode="server"
        sortingMode="server"
        disableColumnFilter={true}
        onPaginationModelChange={handlePaginationChange}
        onSortModelChange={(model) => {
          if (model.length > 0) {
            const sortModel = model[0];
            setSortBy(sortModel.field);
            setSortOrder(sortModel.sort || "asc");
          }
        }}
        onRowClick={(handleRowClick) =>
          openManageOrderModal(handleRowClick.row)
        }
        columnVisibilityModel={Object.fromEntries(showListedFields)}
        onColumnVisibilityModelChange={(newModel) => {
          const newMap = new Map<string, boolean>(Object.entries(newModel));
          setShowListedFields(newMap);
        }}
        hideFooterSelectedRowCount={true}
        pageSizeOptions={[5, 10, 20, 50, 100]}
        localeText={{
          columnsManagementSearchTitle: "Procurar",
          columnsManagementNoColumns: "Sem colunas",
          columnsManagementShowHideAllText: "Mostrar/Ocultar Todas",
          columnsManagementReset: "Repor",
          columnsManagementDeleteIconLabel: "Limpar",
          columnMenuLabel: "Menu",
          columnMenuAriaLabel: (columnName: string) =>
            `Menu da coluna ${columnName}`,
          columnMenuShowColumns: "Mostrar colunas",
          columnMenuManageColumns: "Gerir colunas",
          columnMenuFilter: "Filtrar",
          columnMenuHideColumn: "Ocultar coluna",
          columnMenuUnsort: "Remover ordenação",
          columnMenuSortAsc: "Ordenar por ASC",
          columnMenuSortDesc: "Ordenar por DESC",
          columnMenuManagePivot: "Gerir pivot",
          paginationRowsPerPage: "Linhas por página:",
          paginationDisplayedRows: ({ from, to, count, estimated }) => {
            if (!estimated) {
              return `${from}–${to} de ${count !== -1 ? count : `mais de ${to}`}`;
            }
            const estimatedLabel =
              estimated && estimated > to
                ? `cerca de ${estimated}`
                : `mais de ${to}`;
            return `${from}–${to} de ${count !== -1 ? count : estimatedLabel}`;
          },
        }}
        getCellClassName={(params) => {
          const fieldClasses: Record<string, string> = {
            status: "font-semibold",
            commercial_portfolio:
              "font-mono text-xs text-gray-600 force-text-center",
            customer_id: "font-mono text-xs text-gray-600 force-text-center",
            id: "font-mono",
            offer_external_id: "font-mono",
            created_at: "text-xs text-gray-600",
            updated_at: "text-xs text-gray-600",
            amount: "font-mono text-xs text-gray-600 force-text-right",
          };
          return params.isEditable || params.row
            ? fieldClasses[params.field] || ""
            : "";
        }}
        sx={{ border: 0 }}
      />
    </Paper>
  );
};

export default ListOrderItems;
